import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Pencil, Plus, Trash2, Truck } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AllCountriesSelect } from '@/features/installations/components/all-countries-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  SupplierDeleteConfirmDialog,
  type PendingSupplierDelete,
} from '@/features/suppliers/components/supplier-delete-confirm-dialog'
import { useOrganizationSupplierInstallationsQuery } from '@/features/suppliers/hooks/use-supplier-installations'
import {
  useSupplierOrganizationMutations,
  useSupplierOrganizationsQuery,
} from '@/features/suppliers/hooks/use-supplier-organizations'
import {
  isSupplierOrganizationNameTooLong,
  isSupplierOrganizationNameValid,
} from '@/features/suppliers/lib/supplier-organization-name'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { DialogPortalContainer } from '@/shared/lib/dialog-portal-container'
import { preventDialogDismissForCombobox } from '@/shared/lib/dialog-combobox'

export function SuppliersPage() {
  const { t } = useTranslation(['installations', 'common'])
  const { installationId } = useParams<{ installationId: string }>()
  const navigate = useNavigate()
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('organization', 'update')

  const listQuery = useSupplierOrganizationsQuery()
  const installationsQuery = useOrganizationSupplierInstallationsQuery()
  const mutations = useSupplierOrganizationMutations()

  const installationCountBySupplierId = useMemo(() => {
    const counts = new Map<string, number>()
    for (const installation of installationsQuery.data ?? []) {
      const currentCount = counts.get(installation.supplierOrganizationId) ?? 0
      counts.set(installation.supplierOrganizationId, currentCount + 1)
    }
    return counts
  }, [installationsQuery.data])

  const [createOpen, setCreateOpen] = useState(false)
  const [supplierName, setSupplierName] = useState('')
  const [supplierCountry, setSupplierCountry] = useState('')
  const [pendingDelete, setPendingDelete] = useState<PendingSupplierDelete | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const rows = listQuery.data
  const createNameTooLong = isSupplierOrganizationNameTooLong(supplierName)
  const isCreateNameValid = isSupplierOrganizationNameValid(supplierName)
  const isCreateCountryValid = supplierCountry.trim().length === 2
  const isCreateFormValid = isCreateNameValid && isCreateCountryValid
  const createNameError = createNameTooLong
    ? t('installations:suppliers.nameMaxLengthError')
    : undefined

  const resetCreateForm = () => {
    setSupplierName('')
    setSupplierCountry('')
  }

  const submitCreate = async () => {
    const trimmed = supplierName.trim()
    const country = supplierCountry.trim()
    if (!isCreateNameValid || country.length !== 2) {
      return
    }

    try {
      const created = await mutations.addSupplierOrganization.mutateAsync({
        name: trimmed,
        country: country.toLowerCase(),
      })
      resetCreateForm()
      setCreateOpen(false)
      toast.success(t('installations:suppliers.notifications.createSuccess'))
      if (installationId) {
        navigate(`/dashboard/${installationId}/suppliers/${created.id}/home`)
      }
    } catch {
      toast.error(t('installations:suppliers.notifications.createError'))
    }
  }

  const requestDelete = (item: PendingSupplierDelete) => {
    setPendingDelete(item)
    setDeleteDialogOpen(true)
  }

  const clearPendingDelete = () => {
    setPendingDelete(null)
    setDeleteDialogOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return
    }
    await mutations.deleteSupplierOrganization.mutateAsync(pendingDelete.id)
    clearPendingDelete()
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (rows === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('installations:suppliers.loadError')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('installations:suppliers.title')}</h1>
          <p className="text-muted-foreground">{t('installations:suppliers.description')}</p>
        </div>
        {canEdit && rows.length > 0 && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            {t('common:actions.create')}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
          <Truck className="size-10 text-muted-foreground/50" />
          <div className="space-y-1 text-center">
            <h3 className="text-lg font-semibold">{t('installations:suppliers.emptyTitle')}</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('installations:suppliers.emptyDescription')}
            </p>
          </div>
          {canEdit && (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('common:actions.create')}
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="group/row">
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[55%] whitespace-normal'
                  )}
                >
                  {t('installations:configuration.table.name')}
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'w-[140px] min-w-0 max-w-[140px] text-end tabular-nums'
                  )}
                >
                  {t('installations:suppliers.table.installations')}
                </TableHead>
                {canEdit && (
                  <TableHead
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[120px] min-w-0 max-w-[120px] text-end'
                    )}
                  >
                    {t('installations:configuration.table.actions')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[55%] whitespace-normal'
                    )}
                  >
                    <div className="flex flex-col gap-1">
                      <span>{row.name}</span>
                      {row.localName ? (
                        <span className="text-sm text-muted-foreground">{row.localName}</span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[140px] min-w-0 max-w-[140px] text-end tabular-nums'
                    )}
                  >
                    {installationsQuery.isLoading
                      ? '—'
                      : (installationCountBySupplierId.get(row.id) ?? 0)}
                  </TableCell>
                  {canEdit && (
                    <TableCell
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        'w-[120px] min-w-0 max-w-[120px] text-end'
                      )}
                    >
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={t('installations:suppliers.a11y.editSupplier', {
                            name: row.name,
                          })}
                          asChild
                        >
                          <Link
                            to={`/dashboard/${installationId}/suppliers/${row.id}/home`}
                          >
                            <Pencil className="size-4" />
                          </Link>
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          aria-label={t('installations:suppliers.a11y.deleteSupplier', {
                            name: row.name,
                          })}
                          onClick={() => requestDelete({ id: row.id, label: row.name })}
                          disabled={mutations.deleteSupplierOrganization.isPending}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) {
            resetCreateForm()
          }
        }}
      >
        <DialogContent
          onInteractOutside={preventDialogDismissForCombobox}
          onPointerDownOutside={preventDialogDismissForCombobox}
        >
          <DialogPortalContainer>
            <DialogHeader>
              <DialogTitle>{t('installations:suppliers.dialog.addSupplier')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="supplier-create-name">
                  {t('installations:suppliers.edit.identity.fields.name')}
                  <span className="text-destructive"> *</span>
                </Label>
                <Input
                  id="supplier-create-name"
                  placeholder={t('installations:suppliers.namePlaceholder')}
                  value={supplierName}
                  onChange={(event) => setSupplierName(event.target.value)}
                  aria-required
                  aria-invalid={Boolean(createNameError)}
                />
                {createNameError ? (
                  <p className="text-sm text-destructive">{createNameError}</p>
                ) : null}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="supplier-create-country">
                  {t('common:inputFields.country.label')}
                  <span className="text-destructive"> *</span>
                </Label>
                <AllCountriesSelect
                  id="supplier-create-country"
                  value={supplierCountry}
                  onValueChange={setSupplierCountry}
                  placeholder={t('installations:suppliers.dialog.countryPlaceholder')}
                />
              </div>
            </div>
          </DialogPortalContainer>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateOpen(false)
                resetCreateForm()
              }}
            >
              {t('installations:configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!isCreateFormValid || mutations.addSupplierOrganization.isPending}
              onClick={submitCreate}
            >
              {t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupplierDeleteConfirmDialog
        pendingDelete={pendingDelete}
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clearPendingDelete()
          } else {
            setDeleteDialogOpen(true)
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.deleteSupplierOrganization.isPending}
      />
    </div>
  )
}
